import React from 'react';
import { useRef, useMemo } from 'react';
import { Box, Typography } from '@mui/material';

import { PageHeader, SectionContent, StyledList } from '../../components';
import { COOKIE_POLICY } from '../../data';
import THEME from '../../constants/theme';
import {
  SECTION_SPACING,
  SUBSECTION_SPACING,
  ELEMENT_SPACING
} from '../../constants';
import SEO from '../../components/SEO';
import { seoData } from '../../data/seoData';

const CookiePolicyPage = ({ setRefs }) => {
  const cookiePolicyRef = useRef(null);
  const whatAreCookiesRef = useRef(null);
  const cookieTypesRef = useRef(null);
  const howWeUseRef = useRef(null);
  const managingCookiesRef = useRef(null);
  const thirdPartyCookiesRef = useRef(null);
  const dataRetentionRef = useRef(null);
  const policyChangesRef = useRef(null);
  const contactUsRef = useRef(null);

  const refs = useMemo(
    () => ({
      cookiePolicy: cookiePolicyRef,
      whatAreCookies: whatAreCookiesRef,
      cookieTypes: cookieTypesRef,
      howWeUse: howWeUseRef,
      managingCookies: managingCookiesRef,
      thirdPartyCookies: thirdPartyCookiesRef,
      dataRetention: dataRetentionRef,
      policyChanges: policyChangesRef,
      contactUs: contactUsRef
    }),
    []
  );

  // Update global refs if setRefs function is provided
  React.useEffect(() => {
    if (setRefs && typeof setRefs === 'function') {
      setRefs(refs);
    }
  }, [refs, setRefs]);

  return (
    <>
      <SEO
        title={seoData.cookiePolicy.title}
        content={seoData.cookiePolicy.content}
        keywords={seoData.cookiePolicy.keywords}
        canonical={seoData.cookiePolicy.canonical}
        ogImage={seoData.cookiePolicy.ogImage}
      />
      {/* Page Header */}}
      <PageHeader
        id='hero'
        title={COOKIE_POLICY.TITLE}
        align='left'
        sx={{ mb: SECTION_SPACING }}
      />
      {/* Introduction */}
      <SectionContent
        id='cookiePolicy'
        ref={refs.cookiePolicy}
        initialSpacing={false}
        text={[
          {
            text: COOKIE_POLICY.LAST_UPDATED,
            marginBottom: ELEMENT_SPACING,
            fontStyle: 'italic',
            fontSize: '1.1rem',
            color: THEME.COLORS.NEUTRAL_GRAY_DARK
          },
          {
            text: COOKIE_POLICY.INTRO,
            marginBottom: ELEMENT_SPACING,
            fontSize: '1.3rem'
          }
        ]}
      />
      {/* What are cookies section */}
      <SectionContent
        id='whatAreCookies'
        ref={refs.whatAreCookies}
        title={COOKIE_POLICY.SECTIONS.WHAT_ARE_COOKIES.TITLE}
        titleVariant='TitleMedium'
        titleMargin={`${SUBSECTION_SPACING} 0 ${ELEMENT_SPACING} 0`}
        initialSpacing={false}
        text={COOKIE_POLICY.SECTIONS.WHAT_ARE_COOKIES.PARAGRAPHS.map(
          paragraph => ({
            text: paragraph,
            marginBottom: ELEMENT_SPACING,
            fontSize: '1.3rem'
          })
        )}
      />
      {/* Types of cookies section */}
      <SectionContent
        id='cookieTypes'
        ref={refs.cookieTypes}
        title={COOKIE_POLICY.SECTIONS.COOKIE_TYPES.TITLE}
        titleVariant='TitleMedium'
        titleMargin={`${SUBSECTION_SPACING} 0 ${ELEMENT_SPACING} 0`}
        initialSpacing={false}
        text={[]}
      />
      <Box sx={{ mb: ELEMENT_SPACING }}>
        <StyledList items={COOKIE_POLICY.SECTIONS.COOKIE_TYPES.LIST_ITEMS} />
      </Box>
      {/* How we use cookies section */}
      <SectionContent
        id='howWeUse'
        ref={refs.howWeUse}
        title={COOKIE_POLICY.SECTIONS.HOW_WE_USE.TITLE}
        titleVariant='TitleMedium'
        titleMargin={`${SUBSECTION_SPACING} 0 ${ELEMENT_SPACING} 0`}
        initialSpacing={false}
        text={[
          {
            text: COOKIE_POLICY.SECTIONS.HOW_WE_USE.INTRO,
            marginBottom: ELEMENT_SPACING,
            fontSize: '1.3rem'
          }
        ]}
      />
      <Box sx={{ mb: ELEMENT_SPACING }}>
        <StyledList
          variant='blue'
          items={COOKIE_POLICY.SECTIONS.HOW_WE_USE.LIST_ITEMS}
        />
      </Box>
      {/* Managing cookies section */}
      <SectionContent
        id='managingCookies'
        ref={refs.managingCookies}
        title={COOKIE_POLICY.SECTIONS.MANAGING_COOKIES.TITLE}
        titleVariant='TitleMedium'
        titleMargin={`${SUBSECTION_SPACING} 0 ${ELEMENT_SPACING} 0`}
        initialSpacing={false}
        text={[
          {
            text: COOKIE_POLICY.SECTIONS.MANAGING_COOKIES.INTRO,
            marginBottom: ELEMENT_SPACING,
            fontSize: '1.3rem'
          }
        ]}
      />
      <Box sx={{ mb: ELEMENT_SPACING }}>
        <StyledList
          items={COOKIE_POLICY.SECTIONS.MANAGING_COOKIES.LIST_ITEMS}
        />
      </Box>
      {/* Third-Party Cookies section */}
      <SectionContent
        id='thirdPartyCookies'
        ref={refs.thirdPartyCookies}
        title={COOKIE_POLICY.SECTIONS.THIRD_PARTY.TITLE}
        titleVariant='TitleMedium'
        titleMargin={`${SUBSECTION_SPACING} 0 ${ELEMENT_SPACING} 0`}
        initialSpacing={false}
        linkMappings={COOKIE_POLICY.SECTIONS.THIRD_PARTY.LINKS.map(link => ({
          word: link.TEXT,
          url: link.URL,
          color: THEME.COLORS.PRIMARY_BLUE,
          textDecoration: 'underlined',
          newTab: true
        }))}
        text={COOKIE_POLICY.SECTIONS.THIRD_PARTY.PARAGRAPHS.map(paragraph => ({
          text: paragraph,
          marginBottom: ELEMENT_SPACING,
          fontSize: '1.3rem'
        }))}
      />
      {/* Data Retention section */}
      <SectionContent
        id='dataRetention'
        ref={refs.dataRetention}
        title={COOKIE_POLICY.SECTIONS.DATA_RETENTION.TITLE}
        titleVariant='TitleMedium'
        titleMargin={`${SUBSECTION_SPACING} 0 ${ELEMENT_SPACING} 0`}
        initialSpacing={false}
        text={COOKIE_POLICY.SECTIONS.DATA_RETENTION.PARAGRAPHS.map(
          paragraph => ({
            text: paragraph,
            marginBottom: ELEMENT_SPACING,
            fontSize: '1.3rem'
          })
        )}
      />
      {/* Changes to this Cookie Policy section */}
      <SectionContent
        id='policyChanges'
        ref={refs.policyChanges}
        title={COOKIE_POLICY.SECTIONS.POLICY_CHANGES.TITLE}
        titleVariant='TitleMedium'
        titleMargin={`${SUBSECTION_SPACING} 0 ${ELEMENT_SPACING} 0`}
        initialSpacing={false}
        text={COOKIE_POLICY.SECTIONS.POLICY_CHANGES.PARAGRAPHS.map(
          paragraph => ({
            text: paragraph,
            marginBottom: ELEMENT_SPACING,
            fontSize: '1.3rem'
          })
        )}
      />
      {/* Contact Us section */}
      <SectionContent
        id='contactUs'
        ref={refs.contactUs}
        title={COOKIE_POLICY.SECTIONS.CONTACT_US.TITLE}
        titleVariant='TitleMedium'
        titleMargin={`${SUBSECTION_SPACING} 0 ${ELEMENT_SPACING} 0`}
        initialSpacing={false}
        linkMappings={COOKIE_POLICY.SECTIONS.CONTACT_US.LINKS.map(link => ({
          word: link.TEXT,
          url: link.URL,
          color: THEME.COLORS.PRIMARY_BLUE,
          textDecoration: 'underlined',
          newTab: false
        }))}
        text={COOKIE_POLICY.SECTIONS.CONTACT_US.PARAGRAPHS.map(
          (paragraph, index, array) => ({
            text: paragraph,
            marginBottom:
              index === array.length - 1 ? ELEMENT_SPACING : ELEMENT_SPACING,
            whiteSpace: paragraph.includes('\n') ? 'pre-line' : 'normal',
            fontSize: '1.3rem'
          })
        )}
      />
      {/* Footer note with separator line */}
      <Box
        sx={{
          borderTop: `1px solid ${THEME.COLORS.NEUTRAL_GRAY}`,
          paddingTop: ELEMENT_SPACING,
          paddingBottom: ELEMENT_SPACING,
          mt: 4,
          mb: 2
        }}
      >
        <Typography
          variant='body2'
          sx={{
            fontFamily: THEME.TYPOGRAPHY.FONT_FAMILY.SECONDARY,
            fontSize: '1rem',
            fontStyle: 'italic',
            color: THEME.COLORS.NEUTRAL_GRAY,
            lineHeight: 1.6,
            display: 'block'
          }}
        >
          {COOKIE_POLICY.FOOTER_NOTE}
        </Typography>
      </Box>
    </>
  );
};

export default CookiePolicyPage;
